import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, SectionTitle, Note, Image, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider, Separator, Result, CorrectionBox } from '../../../../components/course/CourseComponents';
import screen1 from '../pomme.jpg';

const Chapitre4Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajout et affichage de la nourriture
        </p>
      </InfoBox>

      <Separator type="text" text="Affichage de la nourriture" color="gray-darker" size="md" />
      <SectionTitle>🍎 1/ Affichage de la nourriture</SectionTitle>

      <p>Il est temps d'afficher une pomme que le serpent pourra manger pour grandir.</p>

      <p>Pour cela, on créé les variables globales <InlineCode>nourritureX</InlineCode> et <InlineCode>nourritureY</InlineCode> qui contiennent la position x et y de la pomme :</p>

      <CodeBlock language="javascript">
{`let nourritureX, nourritureY;`}
      </CodeBlock>

      <p>La pomme a dimension de 40x40 pixels. Afin de la positionner correctement dans le canvas et détecter les collisions plus tard, on crée une variable <InlineCode>rayon</InlineCode> qui contient la taille de la moitié de la pomme :</p>

      <CodeBlock language="javascript">
{`let rayon = 20;`}
      </CodeBlock>

      <p>Ensuite, on crée une fonction qui va déterminer les coordonnées aléatoires de la pomme :</p>
      <p>On utilisera la fonction <InlineCode>random()</InlineCode> qui permet de choisir une valeur aléatoire comprise entre un minimum et un maximum.</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>random(min, max)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Génère un nombre aléatoire. <br/>- Paramètre 1 : valeur minimale<br/> - Paramètre 2 : valeur maximale<br/> - Retourne : un nombre entre min et max</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="tip" title="💡 Position de l'image avec imageMode(CENTER)">
        <p>Pour rappel, grâce à <InlineCode>imageMode(CENTER);</InlineCode> l'origine de l'image est en son centre. Si on affiche la pomme en (0, 0), on ne verra qu'un quart de la pomme.</p>
        <p>Donc pour que la pomme ne soit pas à moitié en dehors de l'écran, on cherche une valeur aléatoire comprise entre <InlineCode>rayon</InlineCode> et <InlineCode>taille de l'écran - rayon</InlineCode> (et pareil pour la hauteur).</p>
        <br/><Image src={screen1} alt="Axe Y inversé" width="200px" height="200px" borderRadius="xl" shadow={true} border={false} className="object-contain" /><br/>
      </Note>

      <p>Ce qui donne :</p>

      <CodeBlock language="javascript">
{`function placerNourriture() {
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}`}
      </CodeBlock>

      <p>On appelle cette fonction dans <InlineCode>setup()</InlineCode> pour placer la première pomme.</p>
      <p>Enfin, on affiche la pomme dans <InlineCode>draw()</InlineCode> :</p>

      <CodeBlock language="javascript">
{`// Affichage de la nourriture
image(imagePomme, nourritureX, nourritureY);`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Affichage de la pomme à une position aléatoire">
        <p>- Crée les variables globales <InlineCode>nourritureX</InlineCode>, <InlineCode>nourritureY</InlineCode> et <InlineCode>rayon</InlineCode> :</p>

        <CodeBlock language="javascript">
{`let nourritureX, nourritureY;
let rayon = 20;`}
        </CodeBlock>

        <p>- A la fin du fichier, crée la fonction <InlineCode>placerNourriture()</InlineCode> qui va déterminer une position aléatoire contenue dans l'écran :</p>

        <CodeBlock language="javascript">
{`function placerNourriture() {
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}`}
        </CodeBlock>

        <p>- Appelle cette fonction dans <InlineCode>setup()</InlineCode> pour déterminer la position de la première pomme :</p>

        <CodeBlock language="javascript">
{`placerNourriture();`}
        </CodeBlock>

        <p>- A la fin de <InlineCode>draw()</InlineCode>, affiche la pomme avec les coordonnées <InlineCode>nourritureX</InlineCode> et <InlineCode>nourritureY</InlineCode> :</p>

        <CodeBlock language="javascript">
{`image(imagePomme, nourritureX, nourritureY);`}
        </CodeBlock>

        <br/>
        <Result>Une pomme s'affiche de manière aléatoire sur l'écran. Relance le jeu plusieurs fois pour vérifier que la pomme change de place.</Result>
      </Note>

      <Separator type="text" text="Détection des collisions" color="gray-darker" size="md" />
      <SectionTitle>🎯 2/ Détection des collisions entre la tête et la pomme</SectionTitle>

      <p>Maintenant, on veut que le serpent puisse manger la pomme.</p>
      <p>Cela revient à savoir si la tête rentre en collision avec la pomme.</p>

      <p>Comme dans le jeu <strong>Améliore ta visée !</strong>, il faut calculer la distance entre la tête et la pomme.
      <br/>Si cette distance est inférieure à 40 pixels, alors c'est que la tête touche la pomme !</p>

      <p><strong>Étapes :</strong></p>

      <p>1. On calcule la distance entre la tête et la pomme grâce à la fonction <InlineCode>dist()</InlineCode> :
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>dist(x1, y1, x2, y2)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Calcule la distance entre deux points. <br/>- Paramètre 1 : position x du premier point<br/> - Paramètre 2 : position y du premier point<br/> - Paramètre 3 : position x du deuxième point<br/> - Paramètre 4 : position y du deuxième point<br/> - Retourne : la distance en pixels</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <br/>La tête a pour coordonnées <InlineCode>posX, posY</InlineCode>, et la nourriture <InlineCode>nourritureX, nourritureY</InlineCode> :</p>

      <CodeBlock language="javascript">
{`let distanceNourriture = dist(posX, posY, nourritureX, nourritureY);`}
      </CodeBlock>

      <p>2. Ensuite, on teste la condition suivante : si la distance est inférieure à 40 pixels, alors c'est que la tête touche la pomme !</p>

      <CodeBlock language="javascript">
{`if (distanceNourriture < rayon * 2) {
  longueur = longueur + nourriture;
  placerNourriture();
}`}
      </CodeBlock>

      <p>Si la condition est vraie, on augmente la longueur du serpent de la valeur <InlineCode>nourriture</InlineCode>, et on place une nouvelle pomme.

      <br/>La variable <InlineCode>nourriture</InlineCode> est une variable globale qu'on pourra modifier pour équilibrer le jeu :</p>

      <CodeBlock language="javascript">
{`let nourriture = 12; // longueur supplémentaire à chaque pomme`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Détection des collisions">
        <p>- Crée la variable globale :</p>

        <CodeBlock language="javascript">
{`let nourriture = 12;`}
        </CodeBlock>

        <p>- À la fin de <InlineCode>draw()</InlineCode>, calcule la distance entre la tête et la pomme. Si la distance est inférieure à 40, augmente la longueur du serpent et replace la pomme ailleurs.</p>

        <CodeBlock language="javascript">
{`let distanceNourriture = dist(posX, posY, nourritureX, nourritureY);
if (distanceNourriture < rayon * 2) {
  longueur += nourriture;
  placerNourriture();
}`}
        </CodeBlock>

        <br/>
        <Result>Quand le serpent passe sur une pomme, sa longueur augmente, et la pomme est replacée ailleurs.</Result>
      </Note>

      <Separator type="text" text="Amélioration du placement" color="gray-darker" size="md" />
      <SectionTitle>⚡ 3/ Amélioration du placement de la pomme</SectionTitle>

      <p>Si tu as testé un peu la version actuelle, tu as peut-être remarqué que la pomme pouvait apparaître sur le corps du serpent.</p>

      <p>Pour éviter ce comportement, on va améliorer la fonction <InlineCode>placerNourriture()</InlineCode> :</p>

      <ol className="list-decimal list-inside space-y-2 ml-4">
        <li>On choisit une position aléatoire pour la pomme.</li>
        <li>On regarde si chaque élément du corps est trop proche de cette position.</li>
        <li>Tant que la distance est trop proche, on choisit une nouvelle position.</li>
        <li>Si la distance est suffisamment grande, on garde cette position et on quitte la fonction.</li> 
      </ol>

      <Note type="warning" title="⚠️ Attention à la boucle infinie">
        <p>Si le serpent prend tout l'écran, on risque de ne jamais trouver de position valide, ce qui créerait une boucle infinie (à éviter absolument !).</p>
      </Note>

      <Note type="tip" title="💡 Garde-fou contre la boucle infinie">
        <p>Pour éviter cela, on limite à 100 essais maximum. Si aucun n'est valide, on place la pomme n'importe où, même si elle est sur le serpent.</p>
        <p>Ce mécanisme de sécurité est un <strong>garde-fou</strong>.</p>
      </Note>

      <p>Voici le code amélioré de la fonction <InlineCode>placerNourriture()</InlineCode> :</p>

      <CodeBlock language="javascript" noCollapse={true}>
{`function placerNourriture() {
  let essais = 0;

  // On teste jusqu'à 100 positions maximum
  while (essais < 100) {
    
    // On choisit une position aléatoire
    let x = random(rayon, width - rayon);
    let y = random(rayon, height - rayon);

    // Par défaut, la position est considérée comme valide
    let positionValide = true;

    // Pour chaque élément du corps, on calcule la distance avec la position de la pomme
    // On parcourt la liste de 8 en 8, car ce n'est pas la peine de tester chaque pixel du corps du serpent.
    for (let i = 0; i < corps.length; i += 8) {
      if (dist(x, y, corps[i].x, corps[i].y) < rayon * 2) {
        // Si c'est trop proche, la position n'est pas valide
        positionValide = false;
        // On arrête de tester
        break;
      }
    }

    // Si aucun élément du corps n'est trop proche, on garde cette position
    if (positionValide) {
      nourritureX = x;
      nourritureY = y;
      // On a trouvé une bonne position, on arrête la fonction
      return;
    }

    // Sinon, on réessaie
    essais++;
  }

  // Si on dépasse 100 essais, on place quand même la nourriture quelque part
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}`}
      </CodeBlock>

      <p>Prends le temps de bien comprendre ce code, je l’ai commenté ligne par ligne.</p>

      <Note type="exercice" title="🧪 À toi de jouer : Améliore le placement de la pomme">
        <p>Remplace la fonction <InlineCode>placerNourriture()</InlineCode> par cette version pour éviter que la pomme apparaisse sur le serpent :</p>

        <CodeBlock language="javascript" noCollapse={true} >
{`//--------------------------------- NOURRITURE ------------------------------------------
function placerNourriture() {
  let essais = 0;
  while (essais < 100) {
    let x = random(rayon, width - rayon);
    let y = random(rayon, height - rayon);
    let positionValide = true;
    for (let i = 0; i < corps.length; i += 8) {
      if (dist(x, y, corps[i].x, corps[i].y) < rayon * 2) {
        positionValide = false;
        break;
      }
    }
    if (positionValide) {
      nourritureX = x;
      nourritureY = y;
      return;
    }
    essais++;
  }

  // Si on dépasse 100 essais, on place quand même la nourriture quelque part
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}`}
        </CodeBlock>

        <br/>
        <Result>La pomme ne devrait plus apparaître sur le serpent, sauf si au bout de 100 essais aucune position valide n'a été trouvée.</Result>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Serpent avec nourriture</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 4 : Snake
let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque

let vitesse = 270; // vitesse du snake en pixels par seconde
let longueur = 16; // longueur initiale du snake
let nourriture = 12; // longueur supplémentaire à chaque pomme

let posX, posY;
let dx = 1, dy = 0;
let corps = [];
let nourritureX, nourritureY;
let rayon = 20;

//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png')
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav')
  sonCroque = loadSound('croque.wav')
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
  posX = 40;
  posY = height / 2;
  placerNourriture();
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  // Déplacement
  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;
  corps.push({ x: posX, y: posY });

  // Taille limite
  while (corps.length > longueur) {
    corps.shift();
  }

  // Affichage corps
  for (let i = 0; i < corps.length; i = i + 4) {
    let p = corps[i];
    image(imageCorps, p.x, p.y);
  }

  // Affichage et animation de la tête
  let imageTete;
  if (frameCount % 100 > 10)  imageTete = imageTete1;
  else imageTete = imageTete2;
  image(imageTete, posX, posY);

  // Affichage nourriture
  image(imagePomme, nourritureX, nourritureY);

  // Collision nourriture
  let distanceNourriture = dist(posX, posY, nourritureX, nourritureY);
  if (distanceNourriture < rayon * 2) {
    longueur = longueur + nourriture;
    placerNourriture();
  }

}

//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {
  if (keyCode === UP_ARROW && dy !== 1) { dx = 0; dy = -1; }
  else if (keyCode === DOWN_ARROW && dy !== -1) { dx = 0; dy = 1; }
  else if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }
  else if (keyCode === RIGHT_ARROW && dx !== -1) { dx = 1; dy = 0; }
}

//--------------------------------- NOURRITURE ------------------------------------------
function placerNourriture() {
  let essais = 0;
  while (essais < 100) {
    let x = random(rayon, width - rayon);
    let y = random(rayon, height - rayon);
    let positionValide = true;
    for (let i = 0; i < corps.length; i += 8) {
      if (dist(x, y, corps[i].x, corps[i].y) < rayon * 2) {
        positionValide = false;
        break;
      }
    }
    if (positionValide) {
      nourritureX = x;
      nourritureY = y;
      return;
    }
    essais++;
  }

  // Si on dépasse 100 essais, on place quand même la nourriture quelque part
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}`}
      </CodeBlock>

      <p>🎯 Bravo ! Dans le prochain chapitre, nous allons ajouter des collisions avec les bords et le corps du serpent !</p>
    </>
  );
};

export default Chapitre4Content; 


